import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, ExternalLink, Instagram, Youtube, Facebook, Globe, Twitter, Music2, BookMarked } from "lucide-react";
import { api, API } from "../lib/api";
import { Wordmark } from "../components/Brand";
import { Ribbon } from "./showcase/Release";
import { PublicSeriesTimelines } from "./showcase/SeriesTimelines";
import { NewsletterSignup } from "./showcase/Signup";
import { InviterBoard } from "./showcase/InviterBoard";
import { FormatFilter, FORMAT_OPTIONS } from "./showcase/FormatFilter";
import { FormatBadge } from "./showcase/FormatBadge";

const SOCIAL_ICON = { website: Globe, instagram: Instagram, x: Twitter, facebook: Facebook, tiktok: Music2, youtube: Youtube, goodreads: BookMarked };

export function Cover({ b, className = "" }) {
  const [err, setErr] = useState(false);
  if (b.has_cover && !err) return <img src={`${API}/public/books/${b.slug}/cover`} alt={`${b.title} cover`} onError={() => setErr(true)} className={`object-cover ${className}`} data-testid={`cover-img-${b.slug}`} />;
  return (
    <div className={`flex flex-col items-center justify-center text-center p-3 bg-gradient-to-b from-amber-900/60 to-slate-950 border border-amber-500/30 overflow-hidden ${className}`} data-testid={`cover-placeholder-${b.slug}`}>
      <BookOpen className="text-amber-300 mb-3" size={22} /><p className="font-display text-sm sm:text-lg leading-tight [overflow-wrap:anywhere] max-w-full">{b.title}</p><p className="text-xs text-slate-400 mt-2 [overflow-wrap:anywhere]">{b.author}</p>
    </div>
  );
}

export function StoreButtons({ b, stores, size = "sm" }) {
  const links = Object.entries(b.store_links || {});
  if (!links.length) return b.release?.status === "new_release" ? null : <p className="text-xs text-slate-500">Coming soon to stores</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`store-links-${b.slug}`}>
      {links.map(([k, url]) => (
        <a key={k} href={url} target="_blank" rel="noopener noreferrer nofollow" data-testid={`store-${k}-${b.slug}`}
          className={`inline-flex items-center gap-1 rounded-lg border border-amber-500/40 text-amber-100 hover:bg-amber-600 hover:border-amber-400 transition-colors ${size === "lg" ? "px-4 py-2 text-sm" : "px-2.5 py-1 text-[11px]"}`}>
          {stores[k] || k}<ExternalLink size={size === "lg" ? 13 : 10} />
        </a>
      ))}
    </div>
  );
}

export function PublicHeader({ back }) {
  return (
    <header className="sticky top-0 z-40 bg-slate-950/75 backdrop-blur-xl border-b border-amber-500/15">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/authors" className="flex items-center gap-3" data-testid="showcase-home-link"><BookOpen className="text-amber-400" size={18} /><Wordmark size="sm" tagline={false} testId="showcase-wordmark" /></Link>
        {back && <Link to="/authors" className="text-sm text-amber-200 hover:text-white flex items-center gap-1" data-testid="showcase-back-link"><ArrowLeft size={14} />All books</Link>}
      </div>
    </header>
  );
}

function AuthorBio({ p }) {
  return (
    <section className="relative overflow-hidden border-b border-amber-500/15" data-testid="author-bio">
      <div className="smoke" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-16 grid md:grid-cols-[auto_1fr] gap-10 items-center">
        <div className="emblem-ring w-44 h-44 sm:w-52 sm:h-52 overflow-hidden bg-black/50 flex items-center justify-center mx-auto">
          {p.has_photo ? <img src={`${API}/public/author/photo`} alt={p.name} className="w-full h-full object-cover rounded-full" data-testid="author-photo" /> : <BookOpen className="text-amber-300" size={40} />}
        </div>
        <div>
          <p className="eyebrow mb-3">About the author</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold" data-testid="author-name">{p.name}</h1>
          {p.headline && <p className="text-base md:text-lg text-amber-200 mt-3" data-testid="author-headline">{p.headline}</p>}
          {p.bio && <p className="text-slate-300 mt-5 max-w-2xl whitespace-pre-line leading-relaxed" data-testid="author-bio-text">{p.bio}</p>}
          <div className="flex gap-3 mt-6">{Object.entries(p.socials).map(([k, url]) => { const I = SOCIAL_ICON[k] || Globe; return <a key={k} href={url} target="_blank" rel="noopener noreferrer nofollow" className="w-9 h-9 flex items-center justify-center rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-600 transition-colors" aria-label={k} data-testid={`social-${k}`}><I size={15} /></a>; })}</div>
        </div>
      </div>
    </section>
  );
}

export default function Showcase() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [params, setParams] = useSearchParams();
  const fmt = FORMAT_OPTIONS.some(([v]) => v === params.get("format")) ? params.get("format") : "all";
  const q = params.get("q") || "";
  const setParam = (k, v) => setParams((p) => { const n = new URLSearchParams(p); if (!v || v === "all") n.delete(k); else n.set(k, v); return n; }, { replace: true });
  useEffect(() => { api.get("/public/showcase").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="showcase-error">The showcase is unavailable right now.</div>;
  if (!d) return <div className="min-h-screen" />;
  const needle = q.trim().toLowerCase();
  const shown = d.books.filter((b) => (fmt === "all" || (b.format || "novel") === fmt) && (!needle || [b.title, b.author, b.series].some((t) => (t || "").toLowerCase().includes(needle))));
  return (
    <div className="min-h-screen" data-testid="showcase-page">
      <PublicHeader />
      <AuthorBio p={d.profile} />
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        <p className="eyebrow mb-3">The library</p>
        <h2 className="font-display text-3xl mb-6" data-testid="showcase-heading">{fmt === "all" ? "All works" : FORMAT_OPTIONS.find(([v]) => v === fmt)[1]}</h2>
        {!!d.books.length && <FormatFilter q={q} setQ={(v) => setParam("q", v)} fmt={fmt} setFmt={(v) => setParam("format", v)} />}
        {!d.books.length && <p className="text-slate-500" data-testid="showcase-empty">New books are on their way.</p>}
        {!!d.books.length && !shown.length && <p className="text-slate-500" data-testid="showcase-no-match">No published works match — try another format or search.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="showcase-grid">
          {shown.map((b, i) => (
            <div key={b.slug} className="group fade-up" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`showcase-book-${b.slug}`}>
              <Link to={`/authors/${b.slug}`} className="block" data-testid={`showcase-open-${b.slug}`}>
                <div className="relative aspect-[2/3] rounded-lg overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.6)] group-hover:shadow-[0_0_36px_rgba(157,78,221,0.45)] transition-shadow"><Ribbon r={b.release} slug={b.slug} /><Cover b={b} className="w-full h-full" /><FormatBadge format={b.format} slug={b.slug} overlay /></div>
                <p className="font-display text-base mt-3 leading-tight group-hover:text-amber-200 transition-colors">{b.title}</p>
              </Link>
              {b.series && <p className="text-[11px] text-amber-300/80 mt-1">{b.series}{b.book_number ? ` · Book ${b.book_number}` : ""}</p>}
              {b.release?.readers_waiting && <p className="text-[11px] text-amber-200 mt-1" data-testid={`readers-waiting-${b.slug}`}>{b.release.readers_waiting.toLocaleString()} readers waiting</p>}
              {b.blurb && <p className="text-xs text-slate-400 mt-2 line-clamp-3">{b.blurb}</p>}
              <div className="mt-3"><StoreButtons b={b} stores={d.stores} /></div>
            </div>
          ))}
        </div>
      </section>
      <PublicSeriesTimelines />
      <InviterBoard />
      <NewsletterSignup />
      <footer className="border-t border-amber-500/15 py-8 text-center text-xs text-slate-500">© {new Date().getFullYear()} {d.profile.name} · Khumgetme Media</footer>
    </div>
  );
}
